import { PencilIcon, PlusIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { motion } from "framer-motion";

import { useReportContext } from "../../../report-context";
import { createTemporaryColumn } from "../utils";

import { cn } from "@/lib/utils";
import { ReportColumn } from "@/types/evaluate";
import { Button } from "@/components/ui/button";

interface HeaderButtonProps {
  onClick: () => void;
  showDraftResume: boolean;
}

const AnimatedHeaderButton = ({ onClick, showDraftResume }: HeaderButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Button
      onClick={onClick}
      variant="outline"
      className={cn(
        "group h-[40px] w-full cursor-pointer rounded-none !border-none px-4 transition-all duration-150 hover:scale-105 active:scale-95",
        showDraftResume
          ? "bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-600 hover:shadow-sm"
          : "hover:bg-blue-50 hover:shadow-sm",
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showDraftResume ? (
        <PencilIcon
          className={`inline-block max-h-4 min-h-4 min-w-4 max-w-4 font-light ${
            showDraftResume ? "text-amber-600" : ""
          }`}
        />
      ) : (
        <motion.div
          animate={
            isHovered
              ? {
                  rotate: 90,
                  scale: 1.1,
                  transition: {
                    type: "spring",
                    stiffness: 300,
                    damping: 15,
                  },
                }
              : {
                  rotate: 0,
                  scale: 1,
                  transition: {
                    type: "spring",
                    stiffness: 300,
                    damping: 15,
                  },
                }
          }
          style={{
            transformOrigin: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "16px",
            height: "16px",
          }}
        >
          <div className="flex items-center justify-center rounded-sm border border-blue-200 bg-blue-100">
            <PlusIcon aria-hidden="true" className="h-4 w-auto text-blue-600" />
          </div>
        </motion.div>
      )}
      <span
        className={cn(
          "ml-2 transition-all duration-150 ",
          showDraftResume
            ? "text-amber-600 group-hover:text-amber-600"
            : "text-blue-600 group-hover:text-blue-600",
        )}
      >
        {showDraftResume ? "Resume Draft" : "Add Column"}
      </span>
    </Button>
  );
};

interface AddStepButtonProps {
  reportId: number | undefined;
  sortedBlueprintColumns: ReportColumn[] | null;
  gridRef: React.RefObject<AgGridReact>;
}

export const useAddStepColumn = ({
  reportId,
  sortedBlueprintColumns,
  gridRef,
}: AddStepButtonProps) => {
  const reportContext = useReportContext();

  const handleAddStep = () => {
    if (!reportId) {
      return;
    }

    // If there's an existing draft, resume editing it
    if (reportContext.persistedDraftColumn) {
      reportContext.setSelectedColumn(reportContext.persistedDraftColumn);
      reportContext.openSidebar();
      return;
    }

    const maxPosition =
      sortedBlueprintColumns && sortedBlueprintColumns.length > 0
        ? Math.max(...sortedBlueprintColumns.map(c => c.position || 0))
        : -1;

    const tempColumn = createTemporaryColumn(
      reportId,
      maxPosition + 1,
      sortedBlueprintColumns?.map(c => c.name),
    );

    reportContext.setSelectedColumn(tempColumn);
    reportContext.openSidebar();
  };

  const isDraftActive = !!(reportContext.selectedColumn && !reportContext.selectedColumn.id);

  const hasDraftColumn = reportContext.hasPersistedDraftColumn;
  const showDraftResume = hasDraftColumn && !isDraftActive;

  useEffect(() => {
    gridRef?.current?.api?.setColumnsVisible(["col_-99999999"], !isDraftActive);
  }, [isDraftActive, gridRef]);

  return {
    header: "New Column",
    name: "new_column",
    id: -99999999,
    pinned: "right" as const,
    suppressColumnsToolPanel: true,
    lockPosition: "right" as const,
    resizable: false,
    cellRenderer: () => (
      <div
        className={cn(
          "flex h-full w-full cursor-pointer items-center justify-center border-l border-gray-200 bg-gray-100 transition-colors duration-150 hover:bg-gray-200",
          hasDraftColumn && "!bg-amber-50 hover:!bg-amber-100",
        )}
        onClick={handleAddStep}
      />
    ),
    headerComponent: () => (
      <AnimatedHeaderButton onClick={handleAddStep} showDraftResume={showDraftResume} />
    ),
    suppressMovable: true,
    minWidth: 144,
    maxWidth: 144,
    headerClass: cn("!p-0 !border-none", showDraftResume ? "text-amber-600 bg-amber-50" : ""),
    cellClass: "!p-0 !border-none",
  };
};
